import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Upload, Search, Image as ImageIcon, Video, Music, Loader2, Check, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { getMedias, uploadMedia, validateMediaType, validateFileSize } from '@/services/api/meta-media';
import type { MediaResponse } from '@/types/media';

interface MediaLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (media: MediaResponse) => void;
    pageId: string;
    mediaType?: 'image' | 'video' | 'audio' | 'all';
}

export default function MediaLibraryModal({
    isOpen,
    onClose,
    onSelect,
    pageId,
    mediaType = 'all'
}: MediaLibraryModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMedia, setSelectedMedia] = useState<MediaResponse | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Query para listar mídias
    const { data: medias, isLoading } = useQuery({
        queryKey: ['medias', mediaType],
        queryFn: () => getMedias(mediaType !== 'all' ? { media_type: mediaType } : undefined),
        enabled: isOpen,
    });

    // Mutation para upload
    const uploadMutation = useMutation({
        mutationFn: (file: File) => uploadMedia({ file, page_id: pageId }),
        onSuccess: (data) => {
            toast({
                title: 'Upload concluído!',
                description: 'Mídia enviada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['medias'] });
            setSelectedMedia(data);
            setIsUploading(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro no upload',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
            setIsUploading(false);
        },
    });

    // Handler de upload
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tipo
        if (!validateMediaType(file, mediaType)) {
            toast({
                variant: 'destructive',
                title: 'Tipo de arquivo inválido',
                description: `Apenas ${mediaType === 'all' ? 'imagens, vídeos e áudios' : mediaType} são permitidos.`,
            });
            return;
        }

        // Validar tamanho (25MB)
        if (!validateFileSize(file, 25)) {
            toast({
                variant: 'destructive',
                title: 'Arquivo muito grande',
                description: 'O tamanho máximo é 25MB.',
            });
            return;
        }

        setIsUploading(true);
        uploadMutation.mutate(file);
    };

    // Filtrar mídias por busca
    const filteredMedias = medias?.filter(media =>
        media.filename.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    // Handler de seleção
    const handleSelect = () => {
        if (selectedMedia) {
            onSelect(selectedMedia);
            onClose();
        }
    };

    // Reset ao fechar
    useEffect(() => {
        if (!isOpen) {
            setSelectedMedia(null);
            setSearchQuery('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const getMediaIcon = (type: string) => {
        switch (type) {
            case 'image': return <ImageIcon className="w-4 h-4" />;
            case 'video': return <Video className="w-4 h-4" />;
            case 'audio': return <Music className="w-4 h-4" />;
            default: return <ImageIcon className="w-4 h-4" />;
        }
    };

    // ✅ Componente de Preview de Mídia
    const MediaPreview = ({ media }: { media: MediaResponse }) => {
        switch (media.media_type) {
            case 'image':
                return (
                    <img
                        src={media.file_url}
                        alt={media.filename}
                        className="w-full h-full object-cover"
                    />
                );

            case 'video':
                return (
                    <div className="relative w-full h-full bg-gray-900">
                        <video
                            src={media.file_url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                        />
                        {/* Overlay com ícone de Play */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                                <Play className="w-6 h-6 text-gray-900 ml-0.5" />
                            </div>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div className="w-full h-full bg-gradient-to-br from-cyan-100 to-pink-100 flex items-center justify-center">
                        <div className="text-center">
                            <Music className="w-12 h-12 text-cyan-600 mx-auto mb-2" />
                            <p className="text-xs text-cyan-900 font-medium">Áudio</p>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        {getMediaIcon(media.media_type)}
                    </div>
                );
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Biblioteca de Mídias</h2>
                        <p className="text-sm text-gray-600 mt-0.5">
                            Escolha uma mídia existente ou faça upload
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="px-6 py-4 border-b border-gray-200 flex gap-3 flex-shrink-0">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Buscar mídias..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>

                    {/* Upload Button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileSelect}
                        accept={
                            mediaType === 'image' ? 'image/*' :
                                mediaType === 'video' ? 'video/*' :
                                    mediaType === 'audio' ? 'audio/*' :
                                        'image/*,video/*,audio/*'
                        }
                        disabled={isUploading}
                    />
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload
                            </>
                        )}
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-64">
                            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
                        </div>
                    ) : filteredMedias.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-600 font-medium">Nenhuma mídia encontrada</p>
                            <p className="text-sm text-gray-500 mt-1">Faça upload da sua primeira mídia</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-4">
                            {filteredMedias.map((media) => (
                                <div
                                    key={media.id}
                                    onClick={() => setSelectedMedia(media)}
                                    className={`
                                        relative aspect-square rounded-lg overflow-hidden cursor-pointer
                                        transition-all duration-200 border-2
                                        ${selectedMedia?.id === media.id
                                            ? 'border-cyan-500 ring-2 ring-cyan-100'
                                            : 'border-transparent hover:border-cyan-200'
                                        }
                                    `}
                                >
                                    {/* ✅ Preview com componente dedicado */}
                                    <MediaPreview media={media} />

                                    {/* Selected Indicator */}
                                    {selectedMedia?.id === media.id && (
                                        <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                                            <Check className="w-4 h-4 text-white" />
                                        </div>
                                    )}

                                    {/* Type Badge */}
                                    <div className="absolute bottom-2 left-2">
                                        <div className="px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-xs text-white flex items-center gap-1">
                                            {getMediaIcon(media.media_type)}
                                            <span className="capitalize">{media.media_type}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>
                        {selectedMedia && (
                            <p className="text-sm text-gray-600">
                                Selecionado: <span className="font-medium">{selectedMedia.filename}</span>
                            </p>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <Button variant="outline" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSelect}
                            disabled={!selectedMedia}
                        >
                            Selecionar
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}